import {useId} from 'react';
import CleaningTypeSelect from './CleaningTypeSelect';
import {keynestLink} from './keynestLink';
import {Button, Form} from 'react-bootstrap';
import {PROPERTY_ROOM_FIELDS, PROPERTY_TIME_OPTIONS} from './propertyDetailsModel';
import {GUEST_KEY_LABELS} from './propertyGuestKeyModel';

const labelFor = value => value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();

function optionsWithCurrent(options, value) {
    return value && !options.includes(value) ? [value, ...options] : options;
}

function TaskFields({id, title, level, items, onChange, disabled, error}) {
    return <section className="property-details-tasks" aria-labelledby={id} data-job-field={level === 'PROPERTY' ? 'checklists' : 'job_tasks'} tabIndex={-1}>
        <div className="property-details-task-heading">
            <span id={id} className="form-label">{title}s</span>
            <Button size="sm" variant="outline-primary" disabled={disabled}
                onClick={() => onChange([...items, {description: '', level, completed: false}])}>+ {title}</Button>
        </div>
        <p className="property-details-task-help">{level === 'PROPERTY' ? 'Property Tasks sync to unfinished Jobs.' : 'Job Tasks apply only to the Job being created.'}</p>
        {error ? <p className="text-danger" role="alert">{error}</p> : null}
        {items.length === 0 ? <p className="property-details-task-empty">No {title}s.</p> : null}
        {items.map((item, index) => <div key={item.id ?? `new-${index}`} className="property-details-task">
            <Form.Control as="textarea" rows={1} size="sm" value={item.description} disabled={disabled}
                aria-label={`${title} ${index + 1}`} placeholder={`${title} description`}
                onChange={event => onChange(items.map((current, currentIndex) => currentIndex === index
                    ? {...current, description: event.target.value} : current))} />
            <Button size="sm" variant="outline-secondary" disabled={disabled} aria-label={`Remove ${title} ${index + 1}`}
                onClick={() => onChange(items.filter((_, currentIndex) => currentIndex !== index))}>×</Button>
        </div>)}
    </section>;
}

export default function PropertyDetailsFields({value, jobTypes, cleaningTypes, consumableTypes, onChange, jobDraft, onJobChange, onEditGuestKey, errors = {}}) {
    const formId = useId();
    const field = (key, next) => onChange({[key]: next});
    const feedback = key => errors[key] ? <Form.Control.Feedback type="invalid" id={`${formId}-${key}-error`}>{errors[key]}</Form.Control.Feedback> : null;
    const errorProps = key => ({isInvalid: Boolean(errors[key]), 'aria-describedby': errors[key] ? `${formId}-${key}-error` : undefined});
    const textField = (key, label, props = {}) => <Form.Group controlId={`${formId}-${key}`} className="property-details-field" data-job-field={key}>
        <Form.Label>{label}</Form.Label>
        <div><Form.Control size="sm" value={value[key]} onChange={event => field(key, event.target.value)} {...props} {...errorProps(key)} />{feedback(key)}</div>
    </Form.Group>;
    const selectField = (key, label, options, display = item => item) => <Form.Group controlId={`${formId}-${key}`} className="property-details-field" data-job-field={key}>
        <Form.Label>{label}</Form.Label>
        <div><Form.Select size="sm" value={value[key]} onChange={event => field(key, event.target.value)} {...errorProps(key)}>
            {optionsWithCurrent(options, value[key]).map(option => <option key={option} value={option}>{display(option) || 'Select…'}</option>)}
        </Form.Select>{feedback(key)}</div>
    </Form.Group>;

    return <div className="property-details-form">
        <div className="property-details-meta">
            <span>Property ID: <strong>#{value.property_id}</strong></span>
            <span>Region: <strong>{value.region || '—'}</strong></span>
        </div>
        <div className="property-details-grid">
            <div className="property-details-full">{textField('full_address', 'Address', {required: true})}</div>
            <div className="property-details-field" data-job-field="cos_customer_name" tabIndex={-1}>
                <span className="form-label">Customer name</span><span className="property-details-readonly">{value.cos_customer_name || '—'}</span>
                {errors.cos_customer_name ? <span className="text-danger">{errors.cos_customer_name}</span> : null}
            </div>
            {textField('phone', 'Phone', {type: 'tel'})}
            {textField('agent_name', 'Agent name')}
            {textField('email', 'Email')}
            {textField('key_number', 'Key number')}
            {jobDraft ? <Form.Group className="property-details-field" data-job-field="cleaning_type_id">
                <Form.Label>Job type</Form.Label>
                <CleaningTypeSelect options={cleaningTypes} value={jobDraft.cleaning_type_id} invalid={Boolean(errors.cleaning_type_id)}
                    onChange={cleaning_type_id => onJobChange({cleaning_type_id})} />
                {errors.cleaning_type_id ? <span className="text-danger">{errors.cleaning_type_id}</span> : null}
            </Form.Group> : selectField('job_type', 'Job category', ['', ...jobTypes])}
            {selectField('check_out', 'Check out', PROPERTY_TIME_OPTIONS)}
            {jobDraft ? <Form.Group controlId={`${formId}-check_in`} className="property-details-field" data-job-field="check_in">
                <Form.Label>Check in</Form.Label>
                <div><Form.Select size="sm" value={jobDraft.check_in} onChange={event => onJobChange({check_in: event.target.value})} {...errorProps('check_in')}>
                    {optionsWithCurrent(PROPERTY_TIME_OPTIONS, jobDraft.check_in).map(time => <option key={time} value={time}>{time || 'Not set'}</option>)}
                </Form.Select>{feedback('check_in')}</div>
            </Form.Group> : null}
            {selectField('consumable_type', 'Consumable', ['', ...consumableTypes], option => option === 'BRING_YOUR_OWN_CONSUMABLE'
                ? 'BYO' : option.toLowerCase().split('_').map(labelFor).join(' '))}
            <div className={`property-details-field${jobDraft ? '' : ' property-details-full'}`}>
                <span className="form-label">Guest Key</span>
                <div className="d-flex align-items-center gap-2"><span className="property-details-readonly">{GUEST_KEY_LABELS[value.guest_key_type] || value.guest_key_type}
                    {['OTHERS', 'KEYNEST'].includes(value.guest_key_type) && value.guest_key_info ? ` · ${value.guest_key_info}` : ''}
                </span>{value.guest_key_type === 'KEYNEST' && keynestLink(value.keynest_url) ? <a href={keynestLink(value.keynest_url)} target="_blank" rel="noopener noreferrer">Open KeyNest</a> : null}{onEditGuestKey ? <Button size="sm" variant="outline-primary" onClick={onEditGuestKey} aria-label="Edit Guest Key">Edit</Button> : null}</div>
            </div>
        </div>
        <div className="property-details-content">
            <Form.Group controlId={`${formId}-description`} className="property-details-description" data-job-field="description">
                <Form.Label>Description</Form.Label>
                <Form.Control as="textarea" rows={7} value={value.description} onChange={event => field('description', event.target.value)} {...errorProps('description')} />
                {feedback('description')}
            </Form.Group>
            <div className="property-details-rooms">
                <div className="form-label" id={`${formId}-rooms`}>Rooms</div>
                <div role="group" aria-labelledby={`${formId}-rooms`}>
                    {PROPERTY_ROOM_FIELDS.map(key => <div key={key} className="property-details-room" data-job-field={key}>
                        <label htmlFor={`${formId}-${key}`}>{labelFor(key)}</label>
                        <Button size="sm" variant="link" disabled={Number(value[key]) <= 0} aria-label={`Decrease ${labelFor(key)}`}
                            onClick={() => field(key, Math.max(0, Number(value[key] || 0) - 1))}>−</Button>
                        <Form.Control id={`${formId}-${key}`} size="sm" type="number" min="0" step="any" value={value[key]}
                            isInvalid={value[key] === '' || !Number.isFinite(Number(value[key])) || Number(value[key]) < 0}
                            aria-describedby={errors[key] ? `${formId}-${key}-error` : undefined}
                            onChange={event => field(key, event.target.value === '' ? '' : Number(event.target.value))} />
                        <Button size="sm" variant="link" aria-label={`Increase ${labelFor(key)}`}
                            onClick={() => field(key, Number(value[key] || 0) + 1)}>+</Button>
                        {errors[key] ? <div id={`${formId}-${key}-error`} className="property-details-room-error text-danger">{errors[key]}</div> : null}
                    </div>)}
                </div>
            </div>
        </div>
        <TaskFields id={`${formId}-tasks`} title="Property Task" level="PROPERTY" items={value.checklists}
            onChange={items => field('checklists', items)} disabled={value.checklist_revision == null}
            error={errors.checklists || (value.checklist_revision == null ? 'Reload property details to edit Property Tasks.' : '')} />
        {jobDraft ? <TaskFields id={`${formId}-job-tasks`} title="Job Task" level="JOB" items={jobDraft.checklists}
            onChange={items => onJobChange({checklists: items})} /> : null}
    </div>;
}
